/**
 * What "É um sinônimo" would teach the catalogue, for one finding.
 *
 * The term is the receita's word for a keyed product, so only a finding that
 * is about one product can teach: an unresolved identity, a dose compared on
 * one keyed line, or a composition with exactly one line missing and one left
 * over. The server picks the product from the stored orçamento on the same
 * three shapes (`synonym-target.ts`); the two lists have to agree, or the
 * screen offers a box whose answer is dropped.
 *
 * A form finding teaches a form instead (#607): the receita's word becomes
 * the account's name for the keyed form, when `checkForm` recorded which one
 * (`teachableForm`, only on a word no form of ours is). The server reads the
 * same field off the stored finding.
 *
 * Everywhere else -- posology, volume, prescritor, paciente -- there is
 * nothing to teach, and the reason is not offered at all (#508).
 */
interface TeachableFinding {
  readonly check: string
  readonly evidence: Readonly<Record<string, unknown>>
}

const one = (value: unknown): string | undefined =>
  Array.isArray(value) && value.length === 1 && typeof value[0] === 'string' ? value[0] : undefined

/**
 * The term to offer, or undefined when the finding cannot teach.
 *
 * `rowName` is the receita's name on the row the finding sits on: a dose
 * finding carries ids, not names.
 */
export function teachableTerm(finding: TeachableFinding, rowName?: string): string | undefined {
  const { check, evidence } = finding
  const said = (term: unknown) =>
    typeof term === 'string' && term.trim() !== '' ? term : undefined

  if (check === 'identity') {
    return typeof evidence['selectedErpCode'] === 'string'
      ? said(evidence['prescribedTerm'])
      : undefined
  }
  if (check === 'dose') {
    return typeof evidence['quotedIngredientId'] === 'string' ? said(rowName) : undefined
  }
  if (check === 'form') {
    return typeof evidence['teachableForm'] === 'string' ? said(evidence['prescribed']) : undefined
  }
  if (check === 'composition') {
    return one(evidence['added']) === undefined ? undefined : said(one(evidence['missing']))
  }
  return undefined
}
